// The engine's total API: legalMoves / applyMove / result (T1.8).
import { cellKey, neighbors, parseKey } from './hex';
import type { Color, GameResult, GameState, Hex, Move, TileId } from './index';
import { BUG_DESTINATIONS } from './bugs/index';
import { pillbugTosses } from './bugs/pillbug';
import { articulationCells, placements } from './rules';
import { handCount, placeTile, removeTop, tileEquals, tileKey, topTile } from './state';
import { hash } from './zobrist';

export class IllegalMoveError extends Error {
  override name = 'IllegalMoveError';
}

function moveKey(m: Move): string {
  switch (m.type) {
    case 'place':
      return `p|${tileKey(m.tile)}|${cellKey(m.to)}`;
    case 'move':
      return `m|${tileKey(m.tile)}|${cellKey(m.from)}|${cellKey(m.to)}`;
    case 'toss':
      return `t|${tileKey(m.by)}|${tileKey(m.tile)}|${cellKey(m.from)}|${cellKey(m.to)}`;
    case 'pass':
      return 'x';
  }
}

// States are immutable values, so legal-move sets can be memoized by identity.
// applyMove validates via legalMoves; without this every apply recomputes what
// the caller usually just computed.
const movesCache = new WeakMap<GameState, Move[]>();

/** Exhaustive legal moves for the side to move. The UI renders ONLY this. */
export function legalMoves(state: GameState): Move[] {
  const cached = movesCache.get(state);
  if (cached) return cached;
  const moves = computeLegalMoves(state);
  movesCache.set(state, moves);
  return moves;
}

function computeLegalMoves(state: GameState): Move[] {
  if (result(state).status !== 'ongoing') return [];

  const moves: Move[] = [...placements(state)];

  // No piece may move until that player's queen is placed.
  if (state.hands[state.toMove].Q === 0) {
    const cuts = articulationCells(state.board); // one-hive, computed once per call
    const stunned = state.lastMoved?.byPillbug ? state.lastMoved.tile : undefined;
    for (const [key, stack] of state.board) {
      const from = parseKey(key);
      const tile = topTile(state.board, from);
      if (!tile || tile.color !== state.toMove) continue;
      if (stunned && tileEquals(stunned, tile)) continue; // pillbug stun (T2.2)
      if (stack.length < 2 && cuts.has(key)) continue; // stack tops are never blocked
      const generate = BUG_DESTINATIONS[tile.kind];
      if (!generate) continue;
      for (const to of generate(state.board, from)) {
        moves.push({ type: 'move', tile, from, to });
      }
    }

    // Pillbug tosses (and mosquito-copied tosses). A toss whose (tile, from, to)
    // duplicates a legal self-move is dropped: UHP cannot tell the two apart and
    // the ambiguity canonicalizes to self-move (DESIGN §2.4).
    const selfKeys = new Set(
      moves
        .filter((m): m is Extract<Move, { type: 'move' }> => m.type === 'move')
        .map((m) => `${tileKey(m.tile)}|${cellKey(m.from)}|${cellKey(m.to)}`),
    );
    for (const [key, stack] of state.board) {
      const pCell = parseKey(key);
      const top = topTile(state.board, pCell);
      if (!top || top.color !== state.toMove) continue;
      const copiesPillbug =
        top.kind === 'M' &&
        stack.length === 1 && // a mosquito on top of the hive is beetle-only
        neighbors(pCell).some((n) => topTile(state.board, n)?.kind === 'P');
      if (top.kind !== 'P' && !copiesPillbug) continue;
      // A tosser stunned by the opponent's pillbug last turn may not use its
      // ability this turn (T2.2 recency rule — cannot move OR toss).
      if (stunned && tileEquals(stunned, top)) continue;
      for (const m of pillbugTosses(state, pCell, top)) {
        const k = `${tileKey(m.tile)}|${cellKey(m.from)}|${cellKey(m.to)}`;
        if (selfKeys.has(k)) continue;
        selfKeys.add(k); // also dedupes the same toss offered by two tossers
        moves.push(m);
      }
    }
  }

  if (moves.length === 0) moves.push({ type: 'pass' });
  return moves;
}

/** Applies a legal move; throws IllegalMoveError on anything else. */
export function applyMove(state: GameState, move: Move): GameState {
  const legal = new Set(legalMoves(state).map(moveKey));
  if (!legal.has(moveKey(move))) {
    throw new IllegalMoveError(`illegal ${move.type} for ${state.toMove} at turn ${state.turn}`);
  }

  let board = state.board;
  let hands = state.hands;
  let lastMoved: GameState['lastMoved'];
  let passCount = state.passCount;

  switch (move.type) {
    case 'place':
      board = placeTile(board, move.to, move.tile);
      hands = handCount(hands, move.tile.color, move.tile.kind, -1);
      lastMoved = { tile: move.tile, byPillbug: false };
      passCount = 0;
      break;
    case 'move':
      board = placeTile(removeTop(board, move.from), move.to, move.tile);
      lastMoved = { tile: move.tile, byPillbug: false };
      passCount = 0;
      break;
    case 'toss':
      board = placeTile(removeTop(board, move.from), move.to, move.tile);
      lastMoved = { tile: move.tile, byPillbug: true };
      passCount = 0;
      break;
    case 'pass':
      lastMoved = undefined;
      passCount += 1;
      break;
  }

  const next: GameState = {
    options: state.options,
    board,
    hands,
    toMove: state.toMove === 'w' ? 'b' : 'w',
    turn: state.toMove === 'b' ? state.turn + 1 : state.turn,
    passCount,
    positionHashes: state.positionHashes, // replaced below with the new hash appended
    ...(lastMoved ? { lastMoved } : {}), // a pass clears lastMoved entirely
  };
  return { ...next, positionHashes: [...state.positionHashes, hash(next)] };
}

function queenCell(state: GameState, color: Color): Hex | undefined {
  for (const [key, stack] of state.board) {
    if (stack.some((t: TileId) => t.kind === 'Q' && t.color === color)) return parseKey(key);
  }
  return undefined;
}

function isSurrounded(state: GameState, cell: Hex): boolean {
  return neighbors(cell).every((n) => state.board.has(cellKey(n)));
}

/** Board outcomes only — resignation etc. live outside the engine (DESIGN §2.3). */
export function result(state: GameState): GameResult {
  const wQueen = queenCell(state, 'w');
  const bQueen = queenCell(state, 'b');
  const wDead = wQueen !== undefined && isSurrounded(state, wQueen);
  const bDead = bQueen !== undefined && isSurrounded(state, bQueen);
  if (wDead && bDead) return { status: 'draw', by: 'surround' };
  if (wDead) return { status: 'won', winner: 'b', by: 'surround' };
  if (bDead) return { status: 'won', winner: 'w', by: 'surround' };

  // Threefold repetition: the current position (same side to move) recorded a
  // third time. positionHashes holds every reached position, newest last.
  const hashes = state.positionHashes;
  const current = hashes[hashes.length - 1];
  if (current !== undefined) {
    let count = 0;
    for (const h of hashes) if (h === current) count += 1;
    if (count >= 3) return { status: 'draw', by: 'repetition' };
  }
  return { status: 'ongoing' };
}

export { moveKey, tileEquals };
